import { useMemo } from "react";

import {
  deriveEarlyRepaymentPlan,
  type IEarlyRepaymentPlan,
} from "./earlyRepayment";
import type { IRepaymentPlanData, IRepaymentPlanEntryData } from "./types";

export type MoneyColumn =
  | "payment"
  | "interest"
  | "principal"
  | "fees"
  | "remainingBalance";

export interface IRepaymentPlanDisplay {
  displayPlan: IEarlyRepaymentPlan;
  hasFees: boolean;
  moneyColumns: MoneyColumn[];
  recurringFees: (entry: IRepaymentPlanEntryData) => number;
  cellValue: (entry: IRepaymentPlanEntryData, key: MoneyColumn) => number;
  totalValue: (key: MoneyColumn) => number | null;
}

const EMPTY_DISPLAY: IRepaymentPlanDisplay = {
  displayPlan: {
    entries: [],
    totals: {
      totalPayment: "0",
      totalInterest: "0",
      totalPrincipal: "0",
      totalAdministrationFee: "0",
      setupFee: "0",
      totalRepaid: "0",
      totalCostOfCredit: "0",
    },
    isEarly: false,
    monthsSaved: 0,
    interestSaved: 0,
    feesSaved: 0,
    grossSaved: 0,
  },
  hasFees: false,
  moneyColumns: ["payment", "interest", "principal", "remainingBalance"],
  recurringFees: () => 0,
  cellValue: () => 0,
  totalValue: () => null,
};

export const useRepaymentPlanDisplay = (
  plan: IRepaymentPlanData | null | undefined,
  months: number,
): IRepaymentPlanDisplay => {
  return useMemo(() => {
    if (!plan) return EMPTY_DISPLAY;

    const fullMonths = plan.entries.length - 1;
    const clampedMonths = Math.min(Math.max(months, 1), fullMonths);
    const displayPlan = deriveEarlyRepaymentPlan(plan, clampedMonths);

    const hasFees =
      plan.hasAdministrationFee || Number(plan.totals.setupFee) > 0;

    const moneyColumns: MoneyColumn[] = [
      "payment",
      "interest",
      "principal",
      ...(hasFees ? (["fees"] as const) : []),
      "remainingBalance",
    ];

    const recurringFees = (entry: IRepaymentPlanEntryData): number =>
      Number(entry.administrationFees);

    const cellValue = (
      entry: IRepaymentPlanEntryData,
      key: MoneyColumn,
    ): number => {
      switch (key) {
        case "fees":
          return (
            recurringFees(entry) +
            (entry.month === 0 ? Number(displayPlan.totals.setupFee) : 0)
          );
        case "payment":
          return Number(entry.payment);
        default:
          return Number(entry[key]);
      }
    };

    const totalValue = (key: MoneyColumn): number | null => {
      const totalRecurringFees = Number(
        displayPlan.totals.totalAdministrationFee,
      );
      switch (key) {
        case "payment":
          return Number(displayPlan.totals.totalPayment);
        case "interest":
          return Number(displayPlan.totals.totalInterest);
        case "principal":
          return Number(displayPlan.totals.totalPrincipal);
        case "fees":
          return totalRecurringFees + Number(displayPlan.totals.setupFee);
        default:
          return null;
      }
    };

    return {
      displayPlan,
      hasFees,
      moneyColumns,
      recurringFees,
      cellValue,
      totalValue,
    };
  }, [plan, months]);
};
